"use client";
import React, { useState, useEffect, useRef } from "react";
import ReactPlayer from "react-player";
import { FaPlay, FaPause } from "react-icons/fa";

type VideoPlayerProps = {
  url: string;
  className?: string;
};

export const VideoPlayer: React.FC<VideoPlayerProps> = ({ className, url }) => {
  const [playing, setPlaying] = useState(false);
  const [showCenterButton, setShowCenterButton] = useState(true);
  const [hideTimer, setHideTimer] = useState<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const togglePlay = () => {
    setPlaying((prev) => !prev);
    setShowCenterButton(true);
  };

  const handleMouseMove = () => {
    setShowCenterButton(true);
    if (hideTimer) {
      clearTimeout(hideTimer);
    }
    const timer = setTimeout(() => {
      setShowCenterButton(false);
    }, 2000);
    setHideTimer(timer);
  };

  useEffect(() => {
    return () => {
      if (hideTimer) {
        clearTimeout(hideTimer);
      }
    };
  }, [hideTimer]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          setPlaying(false); // Pause when out of view
        }
      },
      {
        threshold: 0.1, // at least 10% visible considered "in view"
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      onClick={(e) => e.stopPropagation()}
      onMouseMove={handleMouseMove}
      className={`relative w-full shrink-0 mx-auto h-400px rounded-lg overflow-hidden shadow-lg ${className}`}
    >
      <div className="pointer-events-none h-full">
        <ReactPlayer
          url={url}
          playing={playing}
          width="100%"
          height="100%"
          config={{
            file: {
              attributes: {
                style: { pointerEvents: "none" },
              },
            },
          }}
        />
      </div>

      {showCenterButton && (
        <button
          onClick={togglePlay}
          className="absolute inset-0 m-auto w-16 h-16 flex items-center justify-center bg-black bg-opacity-50 rounded-full text-white text-2xl hover:scale-110 transition-all duration-300 pointer-events-auto"
        >
          {playing ? (
            <FaPause color="var(--primary-color)" />
          ) : (
            <FaPlay color="var(--primary-color)" />
          )}
        </button>
      )}
    </div>
  );
};

export default VideoPlayer;
